import {
  ControlledCheckbox,
  ControlledDateInput,
  ControlledDateOfBirthInput,
  ControlledInput,
} from "components/Card/ControlledInput";
import FutureMedicalCard from "components/Card/FutureMedicalCard";
import { useServiceDOI } from "components/DOIHeaderSelect/useServiceDOI";
import { MaskedInput, TextInput } from "components/inputs";
import ControlledSelect from "components/inputs/Select/ControlledSelect";
import Notes from "components/Notes";
import { fileToNotesEntity } from "components/Notes/notesAdapter";
import { useCoverage } from "components/QuerySection/hooks/useCoverage";
import { GENDERS } from "constants/FormConstants";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { Path } from "react-hook-form";
import { useForm } from "react-hook-form";
import type {
  CostProjectionDataType,
  DocumentManagementDataType,
  FileType,
  MSADataType,
} from "types/DataTypes";
import { SERVICE_TYPE } from "types/DataTypes";
import { useSelector } from "utils/redux";

type Props = {
  onSave: (field: string | null, value: unknown) => void;
  service: MSADataType | CostProjectionDataType | DocumentManagementDataType;
  section: string;
  className?: string;
};

type FormValues = {
  "claimant.first_name": string;
  "claimant.last_name": string;
  "date_of_injury.jurisdiction": string;
  "date_of_injury.wcmsa_zip_code": string;
  "date_of_injury.one_eleven_jurisdiction": string;
  report_date?: string | Date;
  "claimant.ssn": string;
  "claimant.medicare_id": string;
  "claimant.address": string;
  "claimant.state": string;
  "claimant.city": string;
  "claimant.zip_code": string;
  "claimant.birth_date": string | Date | null;
  "claimant.gender": string;
  "claimant.is_medicare_eligible": boolean;
  "claimant.reasonable_expectation": boolean;
  no_coverage: boolean;
  coverage_complete: boolean;
  claimant_chronological_age: string;
  claimant_rated_age: string;
  claimant_life_expectancy: string;
  msa_period: string;
};

/*
FutureMedicalClaimantInformation holds the forms for claimant info and plan coverage checkboxes
*/
const FutureMedicalClaimantInformation = ({
  onSave,
  service,
  section,
  className = "",
}: Props) => {
  const constants = useSelector((state) => state.shared.constants);
  const { selectedDOI } = useServiceDOI(service);
  const [selectedDoiId, setSelectedDoiId] = useState<number | null>(null);

  const {
    control,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      "claimant.first_name": service?.claimant?.first_name || "",
      "claimant.last_name": service?.claimant?.last_name || "",
      // use actual DOI properties instead of ServiceDOI for WCMSA and 111 Jurisdiction
      "date_of_injury.jurisdiction": selectedDOI?.jurisdiction || "",
      "date_of_injury.wcmsa_zip_code": selectedDOI?.wcmsa_zip_code || "",
      "date_of_injury.one_eleven_jurisdiction":
        selectedDOI?.one_eleven_jurisdiction || "",
      report_date: (service as MSADataType)?.report_date || "",
      "claimant.ssn": service?.claimant?.ssn || "",
      "claimant.medicare_id": service?.claimant?.medicare_id || "",
      "claimant.address": service?.claimant?.address || "",
      "claimant.city": service?.claimant?.city || "",
      "claimant.state": service?.claimant?.state || "",
      "claimant.zip_code": service?.claimant?.zip_code || "",
      "claimant.birth_date": service?.claimant?.birth_date || null,
      "claimant.gender": service?.claimant?.gender || "",
      "claimant.is_medicare_eligible":
        service?.claimant?.is_medicare_eligible || false,
      "claimant.reasonable_expectation":
        service?.claimant?.reasonable_expectation || false,
      no_coverage:
        (service as MSADataType | CostProjectionDataType)?.no_coverage || false,
      coverage_complete:
        (service as MSADataType | CostProjectionDataType)?.coverage_complete ||
        false,
      claimant_chronological_age:
        (service as MSADataType)?.claimant_chronological_age?.toString() || "",
      claimant_rated_age:
        (service as MSADataType)?.claimant_rated_age?.toString() || "",
      claimant_life_expectancy:
        (service as MSADataType)?.claimant_life_expectancy?.toString() || "",
      msa_period: (service as MSADataType)?.msa_period?.toString() || "",
    },
  });

  // update form values when selected DOI changes
  useEffect(() => {
    if (selectedDOI) {
      setSelectedDoiId(selectedDOI.id);
      // when toggling between DOIs, always update these fields with the current DOI values
      const doiFields = {
        "date_of_injury.jurisdiction": selectedDOI.jurisdiction,
        "date_of_injury.wcmsa_zip_code": selectedDOI.wcmsa_zip_code,
        "date_of_injury.one_eleven_jurisdiction":
          selectedDOI.one_eleven_jurisdiction,
      } as const;

      Object.entries(doiFields).forEach(([field, value]) => {
        setValue(field as Path<FormValues>, value ?? "");
      });
    }
  }, [selectedDOI, setValue]);

  // check if the service has plan coverages
  const hasPlanCoverages = "plan_coverages" in service;

  const { hasCoverage, hasCoverageValidationErrors } = useCoverage(
    hasPlanCoverages
      ? service.plan_coverages
      : { part_a_b_coverages: [], part_c_d_coverages: [] },
  );

  // memoised so the claimantInfoContent useMemo below actually holds; both
  // callers (MSA + cost projection section pages) pass a useCallback onSave
  const handleSave = useCallback(
    async (field: string, value: unknown) => {
      onSave(field, value);
    },
    [onSave],
  );

  // the service payload may carry either a nested file or a bare file id.
  // if neither is present, fileId is undefined and the notes card is simply
  // not rendered (see operationsNotesEntity) rather than erroring
  const serviceFile = service?.file as number | FileType;
  const fileId =
    typeof serviceFile === "number" ? serviceFile : serviceFile?.id;

  // file-level (operations) notes: the same notes shown on the Operations
  // file view's Notes tab, mirrored on the Future Medical Operations section.
  // deliberately uses fileToNotesEntity rather than serviceToNotesEntity so
  // the entity carries no serviceId - service-scoped notes already have their
  // own Notes tab in Future Medical, and mixing the two would show a
  // different set of notes from the Operations file view
  const operationsNotesEntity = useMemo(
    () => (fileId ? fileToNotesEntity({ id: fileId }) : null),
    [fileId],
  );

  // only the MSA and cost projection pages mount this component today, but
  // the props union also admits document management services, which keep
  // their notes on DocumentManagementSectionPage - exclude them explicitly
  const showOperationsNotes =
    section === "operations" &&
    service?.type !== SERVICE_TYPE.DOCUMENT_MANAGEMENT &&
    operationsNotesEntity !== null;

  const claimantInfoContent = useMemo(
    () => (
      <>
        <FutureMedicalCard heading="Claimant Information">
          <ControlledInput
            control={control}
            name="claimant.first_name"
            label="First Name"
            errors={errors}
            onSave={(value) => handleSave("claimant.first_name", value)}
            componentType={TextInput}
          />
          <ControlledInput
            control={control}
            name="claimant.last_name"
            label="Last Name"
            errors={errors}
            onSave={(value) => handleSave("claimant.last_name", value)}
            componentType={TextInput}
          />
          <ControlledSelect
            key={`jurisdiction-${selectedDoiId}`}
            control={control}
            name="date_of_injury.jurisdiction"
            label="WCMSA State"
            errors={errors}
            options={constants?.STATES || []}
            onSave={(option) =>
              handleSave("date_of_injury.jurisdiction", option?.value)
            }
          />
          <ControlledInput
            key={`wcmsa-zip-${selectedDoiId}`}
            control={control}
            name="date_of_injury.wcmsa_zip_code"
            label="WCMSA Zip Code"
            errors={errors}
            onSave={(value) =>
              handleSave("date_of_injury.wcmsa_zip_code", value)
            }
            componentType={MaskedInput}
            mask="zipcode-nine"
          />
          <ControlledSelect
            key={`111-jurisdiction-${selectedDoiId}`}
            control={control}
            name="date_of_injury.one_eleven_jurisdiction"
            label="111 Jurisdiction"
            errors={errors}
            options={constants?.JURISDICTIONS || []}
            onSave={(option) =>
              handleSave(
                "date_of_injury.one_eleven_jurisdiction",
                option?.value,
              )
            }
          />
          <ControlledDateInput
            control={control}
            name="report_date"
            label="MSA Report Date"
            errors={errors}
            onSave={(value) => handleSave("report_date", value)}
          />
          <ControlledInput
            control={control}
            name="claimant.ssn"
            label="SSN"
            errors={errors}
            onSave={(value) => handleSave("claimant.ssn", value)}
            componentType={MaskedInput}
            mask="ssn"
          />
          <ControlledInput
            control={control}
            name="claimant.medicare_id"
            label="Medicare ID"
            errors={errors}
            onSave={(value) => handleSave("claimant.medicare_id", value)}
            componentType={MaskedInput}
            mask="medicare"
          />
          <ControlledInput
            control={control}
            name="claimant.address"
            label="Claimant's Address"
            errors={errors}
            onSave={(value) => handleSave("claimant.address", value)}
            componentType={TextInput}
          />
          <ControlledInput
            control={control}
            name="claimant.city"
            label="Claimant's City"
            errors={errors}
            onSave={(value) => handleSave("claimant.city", value)}
            componentType={TextInput}
          />
          <ControlledSelect
            control={control}
            name="claimant.state"
            label="State"
            errors={errors}
            options={constants?.STATES || []}
            onSave={(option) => handleSave("claimant.state", option?.value)}
          />
          <ControlledInput
            control={control}
            name="claimant.zip_code"
            label="Zip"
            errors={errors}
            onSave={(value) => handleSave("claimant.zip_code", value)}
            componentType={MaskedInput}
            mask="zipcode-nine"
          />
          <ControlledDateOfBirthInput
            control={control}
            name="claimant.birth_date"
            errors={errors}
            onSave={(value) => handleSave("claimant.birth_date", value)}
          />
          <ControlledSelect
            control={control}
            name="claimant.gender"
            label="Gender"
            errors={errors}
            options={GENDERS}
            onSave={(option) => handleSave("claimant.gender", option?.value)}
          />
          <ControlledCheckbox
            control={control}
            name="claimant.is_medicare_eligible"
            label="Is Medicare Eligible"
            errors={errors}
            onSave={(value) =>
              handleSave("claimant.is_medicare_eligible", value)
            }
          />
          <ControlledCheckbox
            control={control}
            name="claimant.reasonable_expectation"
            label="Reasonable Expectation"
            errors={errors}
            onSave={(value) =>
              handleSave("claimant.reasonable_expectation", value)
            }
          />
          {section === "query" && hasPlanCoverages && (
            <>
              <ControlledCheckbox
                control={control}
                name="no_coverage"
                label="No Coverage"
                errors={errors}
                onSave={(value) => handleSave("no_coverage", value)}
                readOnly={hasCoverage}
                disabledReason={
                  hasCoverage
                    ? "Cannot mark as no coverage if coverage already exists."
                    : undefined
                }
              />
              <ControlledCheckbox
                control={control}
                name="coverage_complete"
                label="Coverage Information Complete"
                errors={errors}
                onSave={(value) => handleSave("coverage_complete", value)}
                readOnly={hasCoverageValidationErrors}
                disabledReason={
                  hasCoverageValidationErrors
                    ? "Please resolve all validation errors on the Plan Coverage Information."
                    : undefined
                }
              />
            </>
          )}
        </FutureMedicalCard>

        {(section === "medical-review" || section === "legal-review") && (
          <FutureMedicalCard heading="Rated Ages and Life Expectancy">
            <ControlledInput
              control={control}
              name="claimant_chronological_age"
              label="Chronological Age"
              errors={errors}
              onSave={(value) =>
                handleSave("claimant_chronological_age", value)
              }
              componentType={MaskedInput}
              mask="age"
            />
            <ControlledInput
              control={control}
              name="claimant_rated_age"
              label="Rated Age"
              errors={errors}
              onSave={(value) => handleSave("claimant_rated_age", value)}
              componentType={MaskedInput}
              mask="age"
            />
            <ControlledInput
              control={control}
              name="claimant_life_expectancy"
              label="Life Expectancy"
              errors={errors}
              onSave={(value) => handleSave("claimant_life_expectancy", value)}
              componentType={MaskedInput}
              mask="age"
            />
            {service.type === "MSA-111" && (
              <ControlledInput
                control={control}
                name="msa_period"
                label="Period"
                errors={errors}
                onSave={(value) => handleSave("msa_period", value)}
                componentType={MaskedInput}
                mask="age"
              />
            )}
          </FutureMedicalCard>
        )}
      </>
    ),
    [
      control,
      errors,
      constants,
      section,
      hasCoverage,
      hasCoverageValidationErrors,
      handleSave,
      service,
      selectedDoiId,
      hasPlanCoverages,
    ],
  );

  return (
    <div className={`FutureMedicalClaimantInformation ${className}`}>
      <div className="swim-headers">Claimant Information</div>
      <div className="swim-column">
        {claimantInfoContent}
        {showOperationsNotes && (
          <Notes
            entity={operationsNotesEntity}
            heading="Operations Notes"
            cardClassNames="future-medical-card"
          />
        )}
      </div>
    </div>
  );
};

export default FutureMedicalClaimantInformation;
